import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import Chart from 'chart.js/auto';
import '@maersk-global/mds-components-core/mc-icon';
import { KPIFrequency, KPITrendComponentInput, TrendsDataPoint } from '../../models/vsc-scorecard.model';
import { buildThemeObserver, createTooltipElement, destroyChartParts, ScoreBand, SCORE_COLOR_TOKENS } from '../chart-utils';

@Component({
  selector: 'app-vsc-kpi-trend-card',
  imports: [CommonModule],
  templateUrl: './vsc-kpi-trend-card.component.html',
  styleUrl: './vsc-kpi-trend-card.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VscKpiTrendCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) data!: KPITrendComponentInput;

  readonly isExpanded = signal(false);
  private chart: Chart | null = null;
  private tooltipEl: HTMLElement | null = null;
  private canvasEl: HTMLCanvasElement | null = null;
  private themeObserver: MutationObserver | null = null;

  @ViewChild('trendChart')
  set chartCanvasRef(el: ElementRef<HTMLCanvasElement> | undefined) {
    if (el) {
      this.canvasEl = el.nativeElement;
      setTimeout(() => this.buildChart(this.canvasEl!), 0);
    } else {
      this.canvasEl = null;
      this.destroyChart();
    }
  }

  ngOnInit(): void {
    this.isExpanded.set(this.data.expanded);
    this.setupThemeObserver();
  }

  ngOnDestroy(): void {
    this.themeObserver?.disconnect();
    this.destroyChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.canvasEl) {
      setTimeout(() => this.buildChart(this.canvasEl!), 0);
    }
  }

  toggle(): void {
    this.isExpanded.update((v) => !v);
  }

  private get isShopView(): boolean {
    return this.data.isShopView ?? true;
  }

  // Selected month = last trend entry (index 7). Null if that month has no data.
  private get selectedMonthData(): TrendsDataPoint | null {
    const last = this.data.trends.at(-1);
    return last?.trendsMeanValuesThisYear === null ? null : (last ?? null);
  }

  // Most recent entry with data — used only for the score band (card colour).
  private get lastAvailableData(): TrendsDataPoint | undefined {
    return [...this.data.trends].reverse().find((t) => t.trendsMeanValuesThisYear !== null);
  }

  // Header main value — null when selected month has no data
  get isWeekly(): boolean {
    return this.data.frequency === KPIFrequency.Weekly;
  }

  get mainValue(): number | null {
    return this.selectedMonthData?.trendsMeanValuesThisYear ?? null;
  }

  // Score X in "Score: X/Y" — null when selected month has no data
  get currentScore(): number | null {
    const d = this.selectedMonthData;
    if (!d) return null;
    return this.isShopView ? d.trendsOutOfFiveDataThisYear : d.trendsWeightedDataThisYear;
  }

  // Y in "Score: X/Y"
  get maxScore(): number {
    return this.isShopView ? (this.data.kpiMaxOutofFiveScore ?? 5) : (this.data.kpiMaxWeightedScore ?? 5);
  }

  get scoreClass(): string {
    return `score--${this.scoreBand}`;
  }

  get progressWidth(): string {
    if (this.currentScore === null) return '0%';
    return `${(this.currentScore / this.maxScore) * 100}%`;
  }

  private get scoresThisYear(): (number | null)[] {
    return this.data.trends.map((t) => (this.isShopView ? t.trendsOutOfFiveDataThisYear : t.trendsWeightedDataThisYear));
  }

  private get scoresPreviousYear(): (number | null)[] {
    return this.data.trends.map((t) => (this.isShopView ? t.trendsOutOfFiveDataPreviousYear : t.trendsWeightedDataPreviousYear));
  }

  private get monthLabels(): string[] {
    return this.data.trends.map((t) => t.month);
  }

  private get meanValuesThisYear(): (number | null)[] {
    return this.data.trends.map((t) => t.trendsMeanValuesThisYear);
  }

  private get meanValuesPreviousYear(): (number | null)[] {
    return this.data.trends.map((t) => t.trendsMeanValuesPreviousYear);
  }

  get trendDirection(): 'improving' | 'declining' | 'stable' {
    const d = this.meanValuesThisYear.filter((v): v is number => v !== null);
    if (d.length < 2) return 'stable';
    const delta = d.at(-1)! - d[0];
    if (delta === 0) return 'stable';
    const increasing = delta > 0;
    const lowerBetter = this.data.isLowerBetter;
    if (increasing && !lowerBetter) return 'improving';
    if (!increasing && lowerBetter) return 'improving';
    return 'declining';
  }

  get trendLabel(): string {
    return { improving: '↑ Improving', declining: '↓ Declining', stable: '→ Stable' }[this.trendDirection];
  }

  private get scoreBand(): ScoreBand {
    const outOfFive = this.lastAvailableData?.trendsOutOfFiveDataThisYear ?? 0;
    if (outOfFive < 2) return 'poor';
    if (outOfFive < 3) return 'average';
    if (outOfFive <= 4.5) return 'good';
    return 'excellent';
  }

  private buildChart(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.destroyChart();

    // Resolve container early so SCSS host-level custom properties are readable via getComputedStyle
    const container = canvas.parentElement ?? document.body;
    container.style.position = 'relative';

    const style = getComputedStyle(document.documentElement);
    const containerStyle = getComputedStyle(container);
    const lineColor = style.getPropertyValue(SCORE_COLOR_TOKENS[this.scoreBand]).trim();
    // Read colour tokens defined in :host SCSS so all colour derivation stays in one place
    const priorYearColor = containerStyle.getPropertyValue('--kpi-prior-year-color').trim();
    const tooltipShadow = containerStyle.getPropertyValue('--kpi-tooltip-shadow').trim();
    const labelColor = style.getPropertyValue('--mds_brand_appearance_neutral_default_text-color').trim();

    const months = this.monthLabels;
    const scores = this.scoresThisYear;
    const scoresPrev = this.scoresPreviousYear;
    const meanThis = this.meanValuesThisYear;
    const meanPrev = this.meanValuesPreviousYear;
    const unit = this.data.kpiUnit;

    // Inline plugin: draws month + score text near each current-year data point
    const dataLabelPlugin = {
      id: 'kpiTrendDataLabels',
      afterDatasetsDraw: (chart: Chart) => {
        const meta0 = chart.getDatasetMeta(0);
        const meta1 = chart.getDatasetMeta(1);
        chart.ctx.save();
        chart.ctx.font = '11px sans-serif';
        chart.ctx.textAlign = 'center';
        chart.ctx.fillStyle = labelColor;
        meta0.data.forEach((point, i) => {
          const mean = meanThis[i];
          if (mean === null) return;
          const otherY = meta1.data[i]?.y ?? point.y;
          // Prior year is above current on screen (smaller y-pixel = higher on screen)
          if (otherY < point.y) {
            // Place label BELOW current dot to keep it away from the prior-year dot above
            chart.ctx.fillText(months[i], point.x, point.y + 16);
            chart.ctx.fillText(`(${mean.toFixed(2)})`, point.x, point.y + 28);
          } else {
            // Standard: label above current dot
            chart.ctx.fillText(months[i], point.x, point.y - 20);
            chart.ctx.fillText(`(${mean.toFixed(2)})`, point.x, point.y - 8);
          }
        });
        chart.ctx.restore();
      },
    };

    // Inline plugin: draws a short vertical dashed line connecting the two hovered data points
    const crosshairPlugin = {
      id: 'kpiTrendCrosshair',
      afterDraw: (chart: Chart) => {
        const active = chart.getActiveElements();
        if (!active.length) return;
        const x = active[0].element.x;
        const yValues = active.map((el) => el.element.y);
        const topY = Math.min(...yValues) - 10;
        const bottomY = Math.max(...yValues) + 10;
        chart.ctx.save();
        chart.ctx.beginPath();
        chart.ctx.setLineDash([4, 4]);
        chart.ctx.strokeStyle = priorYearColor;
        chart.ctx.lineWidth = 1;
        chart.ctx.moveTo(x, topY);
        chart.ctx.lineTo(x, bottomY);
        chart.ctx.stroke();
        chart.ctx.restore();
      },
    };

    const getTooltipEl = (): HTMLElement => {
      this.tooltipEl ??= createTooltipElement(container, tooltipShadow, 'kpi-trend-tooltip');
      return this.tooltipEl;
    };

    const externalTooltip = (context: { chart: Chart; tooltip: any }) => {
      const { chart, tooltip } = context;
      const el = getTooltipEl();

      if (tooltip.opacity === 0) {
        el.style.opacity = '0';
        return;
      }

      const month = (tooltip.title ?? [])[0] ?? '';
      // Filter out null data points (no API data for that period)
      const points: any[] = (tooltip.dataPoints ?? []).filter((p: any) => p.raw !== null);
      if (!points.length) {
        el.style.opacity = '0';
        return;
      }

      let html = `
        <div style="font-weight:700;font-size:12px;margin-bottom:4px;">${month}</div>
        <hr style="border:none;border-top:1px solid var(--mds_brand_appearance_neutral_default_border-color);margin:0 0 6px;">
      `;

      points.forEach((item: any, idx: number) => {
        const isCurrent = item.datasetIndex === 0;
        const label = isCurrent ? 'Current Year' : 'Prior Year';
        const mean = isCurrent ? meanThis[item.dataIndex] : meanPrev[item.dataIndex];
        const score = isCurrent ? scores[item.dataIndex] : scoresPrev[item.dataIndex];
        const unitStr = unit && unit !== 'none' ? ` ${unit}` : '';

        if (idx > 0) {
          html += `<hr style="border:none;border-top:1px solid var(--mds_brand_appearance_neutral_default_border-color);margin:6px 0;">`;
        }

        html += `
          <div style="color:var(--mds_brand_appearance_neutral_weak_text-color);font-size:11px;margin-bottom:2px;">${label}</div>
          <div style="display:flex;justify-content:space-between;align-items:center;gap:16px;">
            <span>${mean == null ? '—' : mean.toFixed(2)}${unitStr}</span>
            <span style="font-weight:500;white-space:nowrap;">Score: ${score == null ? '—' : score.toFixed(2)}</span>
          </div>
        `;
      });

      el.innerHTML = html;

      const tooltipW = el.offsetWidth || 220;
      const canvasW = chart.width;
      const cx = tooltip.caretX;
      const cy = tooltip.caretY;
      const xPos = cx + tooltipW + 20 > canvasW ? cx - tooltipW - 10 : cx + 10;
      el.style.left = `${xPos}px`;
      el.style.top = `${Math.max(0, cy - 20)}px`;
      el.style.opacity = '1';
    };

    this.chart = new Chart(ctx, {
      type: 'line',
      plugins: [dataLabelPlugin, crosshairPlugin],
      data: {
        labels: months,
        datasets: [
          {
            label: 'Current Year',
            data: meanThis,
            borderColor: lineColor,
            backgroundColor: lineColor,
            pointBackgroundColor: lineColor,
            tension: 0.3,
            pointRadius: 3,
            clip: false,
          },
          {
            label: 'Prior Year',
            data: meanPrev,
            borderColor: priorYearColor,
            backgroundColor: priorYearColor,
            pointBackgroundColor: priorYearColor,
            borderDash: [6, 4],
            tension: 0.3,
            pointRadius: 3,
            clip: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 36, left: 20, right: 20, bottom: 30 } },
        // index mode: hovering anywhere on a column shows both datasets together
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: externalTooltip,
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { display: false }, border: { display: false } },
          y: { display: false, min: 0 },
        },
      },
    });
  }

  private destroyChart(): void {
    destroyChartParts(this.chart, this.tooltipEl);
    this.chart = null;
    this.tooltipEl = null;
  }

  private setupThemeObserver(): void {
    this.themeObserver = buildThemeObserver(() => {
      if (this.canvasEl) this.buildChart(this.canvasEl);
    });
  }
}
